// File: /var/www/tpcrm/frontend/src/components/Agent/AgentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  clientsService,
  quotesService,
  commissionsService,
  itinerariesService,
  paymentRemindersService,
  paymentsService,
} from '../../services';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtCurrency(n) {
  const v = Number(n) || 0;
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toLocaleString()}`;
}

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysUntil(d) {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
}

function isSameMonth(dateStr, ref) {
  const d = new Date(dateStr);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
}

function monthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d;
}

function leadTravelerName(itinerary) {
  try {
    const lt = typeof itinerary.lead_traveler === 'string' ? JSON.parse(itinerary.lead_traveler) : itinerary.lead_traveler;
    if (lt && (lt.firstName || lt.lastName)) return `${lt.firstName || ''} ${lt.lastName || ''}`.trim();
  } catch (e) { /* fall through to title */ }
  return itinerary.title || 'Untitled Trip';
}

function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-gray-500', bg: 'bg-gray-100 border-gray-200' },
  sent: { label: 'Sent', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  accepted: { label: 'Accepted', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  deposit_paid: { label: 'Deposit Paid', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  paid: { label: 'Paid', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  converted: { label: 'Converted', color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
  rejected: { label: 'Declined', color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  expired: { label: 'Expired', color: 'text-gray-400', bg: 'bg-gray-100 border-gray-200' },
};

// ─── Sparkline bar chart ────────────────────────────────────────────────────

function SparkBars({ data }) {
  const max = Math.max(1, ...data.map(d => d.revenue));
  return (
    <div className="flex items-end gap-1.5 h-16">
      {data.map((d, i) => {
        const pct = d.revenue / max;
        const isLast = i === data.length - 1;
        return (
          <div key={d.month} className="flex flex-col items-center gap-1 flex-1">
            <div
              className="w-full rounded-t-sm transition-all"
              style={{
                height: `${Math.max(2, Math.round(pct * 52))}px`,
                backgroundColor: isLast ? 'var(--accent)' : 'var(--primary)',
                opacity: isLast ? 1 : 0.3 + pct * 0.5,
              }}
            />
            <div className="text-[9px] text-[var(--muted-foreground)]">{d.month}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Pipeline funnel ────────────────────────────────────────────────────────

function PipelineFunnel({ stages }) {
  const maxCount = Math.max(1, ...stages.map(s => s.count));
  return (
    <div className="space-y-2">
      {stages.map(stage => (
        <div key={stage.label} className="flex items-center gap-3">
          <div className="w-20 text-xs font-medium text-[var(--muted-foreground)] flex-shrink-0">{stage.label}</div>
          <div className="flex-1 h-7 bg-[var(--muted)]/40 rounded-lg overflow-hidden">
            <div
              className={`h-full ${stage.color} rounded-lg transition-all flex items-center px-2.5`}
              style={{ width: `${Math.max(stage.count ? 6 : 0, (stage.count / maxCount) * 100)}%` }}
            >
              {stage.count > 0 && <span className="text-white text-xs font-bold">{stage.count}</span>}
            </div>
          </div>
          <div className="w-16 text-xs text-right font-medium text-[var(--foreground)] flex-shrink-0">{fmtCurrency(stage.value)}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Shared with independent agents via /independent/dashboard (see App.js) -
  // same data/services (all unified, role-scoped server-side), just a
  // different URL prefix for navigation targets.
  const basePath = user?.role === 'independent_agent' ? '/independent' : '/agent';
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientsData, quotesData, commissionsData, itinerariesData, remindersData, paymentsData] = await Promise.all([
        clientsService.getAll(),
        quotesService.getAll(),
        commissionsService.getAll(),
        itinerariesService.getAll(),
        paymentRemindersService.getAll(),
        paymentsService.getAll().catch(() => []), // non-fatal: revenue widgets degrade gracefully
      ]);

      setClients(Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []));
      setQuotes(Array.isArray(quotesData) ? quotesData : (quotesData?.quotes || []));
      setCommissions(Array.isArray(commissionsData) ? commissionsData : (commissionsData?.commissions || []));
      setItineraries(Array.isArray(itinerariesData) ? itinerariesData : (itinerariesData?.itineraries || []));
      setPaymentReminders(Array.isArray(remindersData) ? remindersData : (remindersData?.reminders || []));
      setPayments(Array.isArray(paymentsData) ? paymentsData : (paymentsData?.payments || []));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--accent)' }} />
          <p className="mt-4 text-sm text-[var(--muted-foreground)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="px-5 py-2.5 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const now = new Date();
  const today = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // ── Derived, real-data metrics ──
  const activeQuotes = quotes.filter(q => ['draft', 'sent'].includes(q.status));
  const pipelineValue = activeQuotes.reduce((s, q) => s + (Number(q.total_amount) || 0), 0);

  const completedPayments = payments.filter(p => p.status === 'completed');
  const revenueThisMonth = completedPayments.filter(p => isSameMonth(p.created_at, now)).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const revenueLastMonth = completedPayments.filter(p => isSameMonth(p.created_at, monthsAgo(1))).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const revenueDelta = revenueLastMonth > 0 ? Math.round(((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100) : null;

  const unopenedQuotes = quotes.filter(q => q.status === 'sent' && !(Number(q.view_count) > 0)).length;
  const needsAttention = unopenedQuotes + paymentReminders.length;

  const newClientsThisMonth = clients.filter(c => c.created_at && isSameMonth(c.created_at, now)).length;

  // Quote pipeline funnel
  const stageOf = (status) => {
    if (status === 'draft') return 'Draft';
    if (status === 'sent') return 'Sent';
    if (['accepted', 'deposit_paid', 'paid', 'converted'].includes(status)) return 'Accepted';
    return 'Declined';
  };
  const stageColors = { Draft: 'bg-gray-300', Sent: 'bg-blue-400', Accepted: 'bg-green-400', Declined: 'bg-red-300' };
  const pipelineStages = ['Draft', 'Sent', 'Accepted', 'Declined'].map(label => {
    const matching = quotes.filter(q => stageOf(q.status) === label);
    return { label, count: matching.length, value: matching.reduce((s, q) => s + (Number(q.total_amount) || 0), 0), color: stageColors[label] };
  });

  // Recent quotes (most recent 6)
  const recentQuotes = [...quotes]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  // Upcoming departures (next 60 days)
  const in60Days = new Date(now.getTime() + 60 * 86400000);
  const upcomingTrips = itineraries
    .filter(i => i.travel_start_date && new Date(i.travel_start_date) >= now && new Date(i.travel_start_date) <= in60Days && i.status !== 'cancelled')
    .sort((a, b) => new Date(a.travel_start_date) - new Date(b.travel_start_date))
    .slice(0, 5)
    .map(i => ({
      ...i,
      clientName: leadTravelerName(i),
      nights: i.travel_end_date ? Math.max(0, Math.round((new Date(i.travel_end_date) - new Date(i.travel_start_date)) / 86400000)) : null,
    }));

  // Revenue trend - last 6 months, from real completed payments
  const monthlyRevenue = Array.from({ length: 6 }, (_, i) => {
    const ref = monthsAgo(5 - i);
    const total = completedPayments.filter(p => isSameMonth(p.created_at, ref)).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { month: ref.toLocaleDateString('en-US', { month: 'short' }), revenue: total };
  });
  const sixMonthRevenue = monthlyRevenue.reduce((s, m) => s + m.revenue, 0);

  // Recent activity feed - merged from real quotes / clients / payments
  const activity = [
    ...quotes.filter(q => q.status === 'sent').map(q => ({
      icon: '📧', text: `Quote sent to ${q.client_name || 'client'} — ${q.title || 'Untitled'}`, ts: q.updated_at || q.created_at,
    })),
    ...quotes.filter(q => ['accepted', 'deposit_paid', 'paid'].includes(q.status)).map(q => ({
      icon: '✅', text: `${q.client_name || 'Client'} accepted the ${q.title || 'quote'}`, ts: q.updated_at || q.created_at,
    })),
    ...clients.map(c => ({
      icon: '👤', text: `New client added: ${c.first_name || ''} ${c.last_name || ''}`.trim(), ts: c.created_at,
    })),
    ...completedPayments.map(p => ({
      icon: '💳', text: `Payment received: ${fmtMoney(p.amount)}`, ts: p.created_at,
    })),
  ]
    .filter(a => a.ts)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts))
    .slice(0, 6)
    .map(a => ({ ...a, time: fmtDate(a.ts) }));

  const unreadCommissionMessages = commissions.filter(c => c.has_unread_messages).length;

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">
            Good {now.getHours() < 12 ? 'morning' : now.getHours() < 18 ? 'afternoon' : 'evening'}, {user?.first_name || 'there'}.
          </h1>
          <p className="text-[var(--muted-foreground)] text-sm">{today}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate(`${basePath}/clients`)}
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 border border-[var(--border)] bg-white rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors"
          >
            Clients
          </button>
          <button
            onClick={() => navigate(`${basePath}/quotes/new`)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New Quote
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Clients', value: clients.length, sub: newClientsThisMonth > 0 ? `+${newClientsThisMonth} this month` : 'No new clients yet', iconBg: 'bg-[var(--primary)]', icon: '👥' },
          { label: 'Active Quotes', value: activeQuotes.length, sub: `${fmtMoney(pipelineValue)} pipeline`, iconBg: 'bg-blue-500', icon: '📄' },
          { label: `${now.toLocaleDateString('en-US', { month: 'short' })} Revenue`, value: fmtMoney(revenueThisMonth), sub: revenueDelta !== null ? `${revenueDelta >= 0 ? '↑' : '↓'} ${Math.abs(revenueDelta)}% vs last month` : 'No prior month data', iconBg: 'bg-[var(--accent)]', icon: '💰' },
          { label: 'Needs Attention', value: needsAttention, sub: `${unopenedQuotes} unopened quote${unopenedQuotes !== 1 ? 's' : ''} · ${paymentReminders.length} payment${paymentReminders.length !== 1 ? 's' : ''}`, iconBg: 'bg-amber-500', icon: '⚠️' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white border border-[var(--border)] rounded-2xl p-5">
            <div className={`w-10 h-10 rounded-xl ${kpi.iconBg} flex items-center justify-center flex-shrink-0 text-white mb-4`}>
              <span>{kpi.icon}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-2xl text-[var(--foreground)] mb-1">{kpi.value}</div>
            <div className="text-xs font-semibold text-[var(--foreground)] mb-0.5">{kpi.label}</div>
            <div className="text-xs text-[var(--muted-foreground)]">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Main 2-col grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote pipeline */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
              <div>
                <div className="text-sm font-bold text-[var(--foreground)]">Quote Pipeline</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">All quotes by status</div>
              </div>
              <div className="text-xs text-[var(--muted-foreground)]">{fmtMoney(pipelineValue)} active pipeline</div>
            </div>
            <div className="p-6">
              <PipelineFunnel stages={pipelineStages} />
            </div>
          </div>

          {/* Recent quotes */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
              <div>
                <div className="text-sm font-bold text-[var(--foreground)]">Recent Quotes</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Most recently created</div>
              </div>
              <button onClick={() => navigate(`${basePath}/quotes/new`)} className="text-xs font-semibold text-[var(--accent)] hover:underline">New Quote →</button>
            </div>
            {recentQuotes.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--muted-foreground)]">No quotes yet.</div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {recentQuotes.map(q => {
                  const sc = STATUS_CONFIG[q.status] || STATUS_CONFIG.draft;
                  return (
                    <div
                      key={q.id}
                      className="flex items-center gap-4 px-6 py-3.5 hover:bg-[var(--muted)]/20 transition-colors cursor-pointer"
                      onClick={() => navigate(`${basePath}/quotes/${q.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-[var(--foreground)] truncate">{q.title || `Quote #${q.quote_number || q.id}`}</span>
                          {q.status === 'sent' && !(Number(q.view_count) > 0) && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500" title="Not yet opened by client" />}
                        </div>
                        <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{q.client_name || 'Unknown client'} · {fmtDate(q.created_at)}</div>
                      </div>
                      <div className="text-sm font-semibold text-[var(--foreground)] flex-shrink-0">{q.total_amount > 0 ? fmtMoney(q.total_amount) : '—'}</div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ${sc.bg} ${sc.color}`}>{sc.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upcoming departures */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
              <div>
                <div className="text-sm font-bold text-[var(--foreground)]">Upcoming Departures</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Next 60 days</div>
              </div>
              <span className="px-2.5 py-1 bg-[var(--accent)]/10 text-[var(--accent)] text-xs font-semibold rounded-full">{upcomingTrips.length} trip{upcomingTrips.length !== 1 ? 's' : ''}</span>
            </div>
            {upcomingTrips.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--muted-foreground)]">No departures in the next 60 days.</div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {upcomingTrips.map(trip => {
                  const du = daysUntil(trip.travel_start_date);
                  const isUrgent = du !== null && du <= 14;
                  return (
                    <div key={trip.id} className="flex items-center gap-4 px-6 py-3.5">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white ${isUrgent ? 'bg-[var(--accent)]' : 'bg-[var(--primary)]'}`}>
                        {initials(trip.clientName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--foreground)] truncate">{trip.clientName}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-[var(--muted-foreground)] truncate">✈️ {trip.title}</span>
                          {trip.nights !== null && <span className="text-xs text-[var(--muted-foreground)] flex-shrink-0">· {trip.nights} nights</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`text-sm font-bold ${isUrgent ? 'text-[var(--accent)]' : 'text-[var(--foreground)]'}`}>{fmtDate(trip.travel_start_date)}</div>
                        <div className={`text-xs mt-0.5 ${isUrgent ? 'text-[var(--accent)] font-semibold' : 'text-[var(--muted-foreground)]'}`}>
                          {du === 0 ? 'Today!' : du === 1 ? 'Tomorrow' : `${du} days`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Quick actions */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border)]">
              <div className="text-sm font-bold text-[var(--foreground)]">Quick Actions</div>
            </div>
            <div className="p-4 space-y-2.5">
              <button
                onClick={() => navigate(`${basePath}/quotes/new`)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
                Create New Quote
              </button>
              <button
                onClick={() => navigate(`${basePath}/clients`)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--muted)]/60 border border-[var(--border)] rounded-xl text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
                Manage Clients
              </button>
              <button
                onClick={() => navigate(`${basePath}/commissions`)}
                className="relative w-full flex items-center gap-3 px-4 py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                Submit Commission
                {unreadCommissionMessages > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                    {unreadCommissionMessages}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate(`${basePath}/payment-links`)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--muted)]/60 border border-[var(--border)] rounded-xl text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                Send Payment Link
              </button>
            </div>
          </div>

          {/* Revenue trend */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
              <div>
                <div className="text-sm font-bold text-[var(--foreground)]">Revenue Trend</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Last 6 months</div>
              </div>
              <div className="text-right">
                <div style={{ fontFamily: 'var(--font-display)' }} className="text-lg text-[var(--foreground)]">{fmtCurrency(sixMonthRevenue)}</div>
              </div>
            </div>
            <div className="px-5 py-4">
              <SparkBars data={monthlyRevenue} />
            </div>
          </div>

          {/* Payment reminders */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--border)]">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
              <div className="text-sm font-bold text-[var(--foreground)]">Payment Reminders</div>
              <span className="ml-auto px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full border border-amber-200">{paymentReminders.length}</span>
            </div>
            {paymentReminders.length === 0 ? (
              <div className="px-5 py-6 text-center text-xs text-[var(--muted-foreground)]">No upcoming payment reminders.</div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {paymentReminders.slice(0, 5).map(r => {
                  const du = daysUntil(r.due_date);
                  const clientName = `${r.client_first_name || ''} ${r.client_last_name || ''}`.trim() || 'Client';
                  return (
                    <div key={r.id} className="px-5 py-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-[var(--foreground)] truncate">{clientName}</div>
                          <div className="text-xs text-[var(--muted-foreground)] truncate">{r.itinerary_title || 'Trip'}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold text-[var(--foreground)]">{fmtMoney(r.payment_amount)}</div>
                          {du !== null && (
                            <div className={`text-xs font-semibold ${du <= 7 ? 'text-red-500' : du <= 14 ? 'text-amber-600' : 'text-[var(--muted-foreground)]'}`}>
                              {du < 0 ? 'Overdue' : `${du} days`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${du !== null && du <= 7 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                          {r.payment_type} · due {fmtDate(r.due_date)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Activity feed */}
          <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border)]">
              <div className="text-sm font-bold text-[var(--foreground)]">Recent Activity</div>
            </div>
            {activity.length === 0 ? (
              <div className="px-5 py-6 text-center text-xs text-[var(--muted-foreground)]">No recent activity.</div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {activity.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 px-5 py-3">
                    <span className="text-base flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[var(--foreground)] leading-relaxed">{item.text}</div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{item.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
